import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Show, useUser } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { AuthModal } from "@/components/ui/auth-modal";
import { LazyVideo } from "@/components/ui/lazy-video";
import { ArrowRight, Gift, Loader2 } from "lucide-react";
import { IconClapperboard, IconStarBurst, IconCoin } from "@/components/ui/cartoon-icons";
import { api, buildQueryString } from "@/lib/api";
import { registerGenerationId } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { useFreeH3PromoStatus } from "@/pages/free-gen-page";
import { isExpired } from "@/config/retention";
import type { Video } from "@/types";

const FEATURES = [
  { Icon: IconClapperboard, label: "Generate Video", desc: "Text or image to video" },
  { Icon: IconStarBurst, label: "Create Character", desc: "Generate or edit characters" },
];

function CollageBackground() {
  return (
    <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url(/collage.png)",
          backgroundSize: "100% auto",
          backgroundRepeat: "repeat-y",
          backgroundPosition: "top center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#f3f0fb]/80 via-[#f3f0fb]/55 via-55% to-[#f3f0fb]" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 50% at 50% 30%, rgba(243,240,251,0.5) 0%, transparent 70%)" }} />
    </div>
  );
}

function MyRecentCreations() {
  const { user, isSignedIn } = useUser();
  const { hasPurchased } = useUserStore();
  const { data } = useQuery({
    queryKey: ["home-recent-videos", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        `/videos${buildQueryString({ limit: "6" })}`,
      );
    },
    enabled: !!user?.id && isSignedIn,
    refetchInterval: (query) => {
      const vids = query.state.data?.videos ?? [];
      const busy = vids.some((v) => v.status === "pending" || v.status === "processing");
      return busy ? 3000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const videos = data?.videos ?? [];
  const inFlight = videos.filter((v) => v.status === "pending" || v.status === "processing");
  const completed = videos.filter((v) => v.status === "completed" && v.url && !isExpired(v.created_at));
  const recent = [...inFlight, ...completed].slice(0, 3);

  const inFlightIds = inFlight.map((v) => v.id).join(",");
  useEffect(() => {
    if (!inFlightIds) return;
    for (const id of inFlightIds.split(",")) registerGenerationId(id);
  }, [inFlightIds]);

  if (!isSignedIn || recent.length === 0) return null;

  return (
    <section className="relative z-10 px-5 md:px-8 pb-14 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-5 gap-3">
        <div>
          <h2
            className="text-xl md:text-2xl text-[#241d3a] tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}
          >
            YOUR RECENT CREATIONS
          </h2>
          {inFlight.length > 0 && (
            <p className="text-[11px] text-[#241d3a]/45 font-semibold mt-0.5">
              Generating — updates automatically
            </p>
          )}
        </div>
        <Link
          to="/assets"
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border-2 border-[#2a2342] bg-[#b8a5f0] text-[12px] font-extrabold text-[#241d3a] shadow-[2px_2px_0_rgba(42,35,66,0.16)] hover:shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all"
        >
          My Creations <ArrowRight className="size-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2.5 md:gap-3">
        {recent.map((v) => {
          const isBusy = v.status === "pending" || v.status === "processing";
          if (isBusy) {
            return (
              <Link
                key={v.id}
                to={`/generation/${v.id}`}
                className="group flex flex-col items-center justify-center gap-2 rounded-2xl overflow-hidden border-2 border-[#2a2342] shadow-[3px_3px_0_rgba(42,35,66,0.18)] bg-white relative aspect-square hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[5px_5px_0_rgba(42,35,66,0.18)] transition-all"
              >
                <Loader2 className="size-6 text-[#b8a5f0] animate-spin" />
                <span className="text-[11px] font-extrabold text-[#241d3a]/50">Generating…</span>
              </Link>
            );
          }
          // The API only sends the copy this user may see: watermarked without a purchase.
          const displayUrl = v.url;
          return (
            <Link
              key={v.id}
              to="/assets"
              className="group block rounded-2xl overflow-hidden border-2 border-[#2a2342] shadow-[3px_3px_0_rgba(42,35,66,0.18)] bg-white relative aspect-square hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[5px_5px_0_rgba(42,35,66,0.18)] transition-all"
            >
              {v.media_type === "IMAGE" ? (
                <img src={displayUrl ?? undefined} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <LazyVideo src={displayUrl ?? ""} className="w-full h-full object-cover" />
              )}
            </Link>
          );
        })}
      </div>
      {!hasPurchased && (
        <Link
          to="/billing"
          className="mt-4 flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl border-2 border-[#2a2342] bg-[#b8a5f0] text-[15px] font-extrabold text-[#241d3a] shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:shadow-[5px_5px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all"
        >
          Buy a pack to generate! <ArrowRight className="size-4" />
        </Link>
      )}
    </section>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const { user } = useUser();
  const { hasPurchased } = useUserStore();
  const { data: promo } = useFreeH3PromoStatus(user?.id);
  const showFreeGenNotice = !!promo?.eligible && hasPurchased;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/create-cartoon${prompt.trim() ? `?prompt=${encodeURIComponent(prompt.trim())}` : ""}`);
  };

  return (
    <div className="relative overflow-hidden -mt-[var(--navbar-h)]">
      <CollageBackground />

      <section className="relative z-10 px-5 md:px-8 pt-[calc(var(--navbar-h)+3.5rem)] md:pt-[calc(var(--navbar-h)+6rem)] pb-14 max-w-4xl mx-auto text-center">
        {showFreeGenNotice && (
          <Link
            to="/free-gen"
            className="mb-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-2xl border-2 border-[#2a2342] bg-[#b8a5f0]/35 px-5 py-4 text-left shadow-[3px_3px_0_rgba(42,35,66,0.16)] hover:shadow-[4px_4px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] transition-all animate-fade-in-up"
          >
            <div className="size-11 rounded-xl bg-[#7c5cf0] text-white flex items-center justify-center shrink-0 border-2 border-[#2a2342]">
              <Gift className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#7c5cf0] mb-0.5">
                Limited time · Purchasers only
              </p>
              <p className="text-[17px] font-extrabold text-[#241d3a] tracking-tight">
                Free Generation — 15 Second
              </p>
              <p className="text-[12px] text-[#241d3a]/60 font-semibold mt-0.5">
                One free Supercharged video with sound. Claim it before it&apos;s gone.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[13px] font-extrabold text-[#241d3a] bg-[#b8a5f0] border-2 border-[#2a2342] px-3.5 py-2 rounded-xl shrink-0 self-start sm:self-center shadow-[2px_2px_0_rgba(42,35,66,0.16)]">
              Claim free gen
              <ArrowRight className="size-3.5" />
            </span>
          </Link>
        )}

        <h1 className="text-[clamp(3.5rem,12vw,8.5rem)] text-[#241d3a] tracking-[-0.05em] leading-[0.85] mb-6 animate-fade-in-up" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>
          CREATE
          <br />
          VIDEOS
          <br />
          <span className="text-gradient">&amp; IMAGES.</span>
        </h1>

        <p className="text-[#241d3a]/70 text-base md:text-lg font-bold max-w-md mx-auto mb-8 animate-fade-in-up delay-100">
          The AI platform for video and image generation. Characters, scenes, and animations, all created for you.
        </p>

        <form onSubmit={handleSubmit} className="relative w-full max-w-lg mx-auto mb-8 animate-fade-in-up delay-200">
          <div className="relative flex items-center rounded-2xl bg-white border-2 border-[#2a2342] shadow-[3px_3px_0_rgba(42,35,66,0.18)] focus-within:shadow-[5px_5px_0_rgba(42,35,66,0.18)] transition-all">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe your video..."
              className="flex-1 bg-transparent px-6 py-3.5 text-[15px] font-semibold text-[#241d3a] placeholder:text-[#241d3a]/30 focus:outline-none"
            />
            <button
              type="submit"
              className="mr-2 flex items-center gap-1.5 px-5 py-2 rounded-[12px] border-2 border-[#2a2342] bg-[#b8a5f0] text-[13px] font-extrabold text-[#241d3a] transition-all shadow-[2px_2px_0_rgba(42,35,66,0.16)] hover:shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:bg-[#c8b8f5] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)]"
            >
              Create <ArrowRight className="size-3.5" />
            </button>
          </div>
        </form>

        <div className="flex items-center justify-center gap-3 flex-wrap animate-fade-in delay-300 mb-12">
          {FEATURES.map((f) => (
            <div key={f.label} className="inline-flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-white border-2 border-[#2a2342] shadow-[2px_2px_0_rgba(42,35,66,0.16)] text-[12px] font-extrabold text-[#241d3a]">
              <f.Icon className="size-7" />
              <div className="text-left">
                <p className="leading-tight">{f.label}</p>
                <p className="text-[10px] text-[#241d3a]/40 font-bold">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Your recent creations (signed-in, has generations) */}
      <MyRecentCreations />

      <section className="relative z-10 px-5 md:px-8 pb-16 max-w-md mx-auto animate-fade-in delay-500">
        <div className="bg-white rounded-3xl border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] p-8 text-center">
          <p className="text-2xl text-[#241d3a] tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>START CREATING</p>
          <p className="text-[13px] text-[#241d3a]/40 font-semibold mb-6 flex items-center justify-center gap-1"><IconCoin className="size-3.5" />42 free tokens on signup. No credit card.</p>
          <Show when="signed-out">
            <CtaAuthButton />
          </Show>
          <Show when="signed-in">
            <Link to="/create-cartoon" className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl border-2 border-[#2a2342] bg-[#b8a5f0] text-[14px] font-extrabold text-[#241d3a] shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:shadow-[5px_5px_0_rgba(42,35,66,0.18)] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:bg-[#c8b8f5] active:translate-x-[2px] active:translate-y-[2px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all">
              Generate Video <ArrowRight className="size-4" />
            </Link>
          </Show>
        </div>
      </section>
    </div>
  );
}

function CtaAuthButton() {
  const [showAuth, setShowAuth] = useState(false);
  return (
    <>
      <button
        onClick={() => setShowAuth(true)}
        className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl border-2 border-[#2a2342] bg-[#b8a5f0] text-[14px] font-extrabold text-[#241d3a] shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:shadow-[5px_5px_0_rgba(42,35,66,0.18)] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:bg-[#c8b8f5] active:translate-x-[2px] active:translate-y-[2px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all"
      >
        Get Started Free <ArrowRight className="size-4" />
      </button>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}
